import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IRequestError from '../../../dtos/IRequestError';
import { useAuth } from '../../../hooks/auth';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import CreateTournamentButton, {
  TournamentSchoolNotice,
} from '../../../components/Buttons/CreateTournamentButton';
import TournamentsBriefing from '../TournamentsBriefing';
import {
  BlobIllustration,
  MiniBotIcon,
  TrophyIcon,
} from '../../../assets/svg/SVGComponents';
import {
  CATEGORY_NUMBER_TO_LABEL,
  MODALITY_SLUG_TO_NAME,
} from '../../Tournament/CreateTournament';
import {
  ActionButtonsRow,
  CategoryBadge,
  CategoryBadgeRow,
  Container,
  HeaderContent,
  LoadingWrapper,
  MainCard,
  MainCardContent,
  ModalitiesLabel,
  ModalitiesRow,
  ModalityCard,
  ModalityImage,
  ModalityInfo,
  ModalityMeta,
  ModalityName,
  Spinner,
  TournamentInfo,
  TournamentList,
  TournamentMeta,
  TournamentStatusBadge,
  TournamentTitle,
  TrophyImageWrapper,
} from './styles';

interface ITournamentCategory {
  id: string;
  number: number;
  name?: string;
}

interface ITournamentModality {
  id: string;
  modality: string;
  max_rounds?: number;
  time_limit_seconds?: number;
  categories?: ITournamentCategory[];
}

interface ITournamentListItem {
  id: string;
  name: string;
  date?: string | null;
  end_date?: string | null;
  school_id?: string | null;
  icon_url?: string | null;
  color?: string | null;
  status?: string | null;
  modalities: ITournamentModality[];
}

const TOURNAMENT_STATUS_LABEL: Record<string, string> = {
  planning: 'Em planejamento',
  planned: 'Em planejamento',
  open: 'Aberto',
  completed: 'Concluído',
};

const MODALITY_IMAGES: Record<string, string> = {
  robo_pintor:
    'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/Group+14045%402x.png',
  seguidor_de_linha:
    'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/illustration-seguidor+de+trilha-2x.png',
};

const TROPHY_IMAGE =
  'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/trophy-podium.png';

const formatRoundDuration = (timeLimitSeconds?: number): string => {
  if (!timeLimitSeconds || timeLimitSeconds <= 0) {
    return '-';
  }
  if (timeLimitSeconds < 60) {
    return `${timeLimitSeconds}s`;
  }
  const minutes = Math.floor(timeLimitSeconds / 60);
  const seconds = timeLimitSeconds % 60;
  if (seconds === 0) {
    return `${minutes} min`;
  }
  return `${minutes} min ${seconds}s`;
};

const formatDate = (
  startDateStr?: string | null,
  endDateStr?: string | null,
): string => {
  if (!startDateStr) return '';
  try {
    const startDate = new Date(startDateStr);
    const endDate = endDateStr ? new Date(endDateStr) : null;

    const sameDay =
      endDate != null &&
      startDate.getFullYear() === endDate.getFullYear() &&
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getDate() === endDate.getDate();

    const formatDateWithoutWeekday = (date: Date) =>
      date.toLocaleDateString('pt-BR', {
        day: 'numeric',
        month: 'long',
      });

    const formatHourLabel = (date: Date) => {
      const hours = date.getHours();
      const minutes = date.getMinutes();
      return minutes === 0
        ? `${hours}h`
        : `${hours}h${String(minutes).padStart(2, '0')}`;
    };

    if (endDate && !sameDay) {
      return `De ${formatDateWithoutWeekday(startDate)} às ${formatHourLabel(startDate)} até ${formatDateWithoutWeekday(endDate)} às ${formatHourLabel(endDate)}.`;
    }

    const formattedDate = startDate.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      weekday: 'long',
    });
    const formattedStartTime = startDate.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
    const formattedEndTime = endDate
      ? endDate.toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
        })
      : null;

    if (formattedEndTime) {
      return `${formattedDate}, ${formattedStartTime} às ${formattedEndTime}.`;
    }
    return `${formattedDate}, ${formattedStartTime}`;
  } catch {
    return startDateStr;
  }
};

const ListTournaments: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user, isSessionExpired } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [tournaments, setTournaments] = useState<ITournamentListItem[]>([]);

  const loadTournaments = useCallback(async () => {
    if (!user.school?.id) {
      setTournaments([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.get<ITournamentListItem[]>('/tournaments', {
        params: { school_id: user.school.id },
      });

      setTournaments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      if (!(await isSessionExpired(err as IRequestError))) {
        addToast({
          type: 'error',
          expression: 'Ops.',
          description: 'Não foi possível carregar os torneios.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [addToast, isSessionExpired, user.school?.id]);

  useEffect(() => {
    loadTournaments();
  }, [loadTournaments]);

  if (isLoading) {
    return (
      <Container>
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      </Container>
    );
  }

  if (tournaments.length === 0) {
    return <TournamentsBriefing />;
  }

  return (
    <Container>
      <HeaderContent>
        <TrophyIcon width="2rem" stroke="#FF9800" strokeWidth={3} fill="none" />
        <h1>Torneios</h1>
        <CreateTournamentButton label="Criar um torneio local" />
      </HeaderContent>

      <TournamentSchoolNotice />

      <TournamentList>
        {tournaments.map(tournament => {
          const dateLabel = formatDate(tournament.date, tournament.end_date);
          return (
            <MainCard key={tournament.id}>
              <MainCardContent>
                <TrophyImageWrapper>
                  <img src={tournament.icon_url ?? TROPHY_IMAGE} alt="Troféu" />
                  <BlobIllustration
                    width="100%"
                    height="100%"
                    fill={tournament.color ?? '#e0e0e0'}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                </TrophyImageWrapper>

                <TournamentInfo>
                  <TournamentTitle>{tournament.name}</TournamentTitle>
                  {tournament.status && (
                    <TournamentStatusBadge $status={tournament.status}>
                      {TOURNAMENT_STATUS_LABEL[tournament.status] ??
                        tournament.status}
                    </TournamentStatusBadge>
                  )}
                  {dateLabel && <TournamentMeta>{dateLabel}</TournamentMeta>}
                  <ModalitiesLabel>modalidades:</ModalitiesLabel>

                  <ModalitiesRow>
                    {(tournament.modalities ?? []).map(mod => {
                      const modalityName =
                        MODALITY_SLUG_TO_NAME[mod.modality] ?? mod.modality;
                      const imgSrc = MODALITY_IMAGES[mod.modality];
                      const maxRoundsLabel =
                        typeof mod.max_rounds === 'number'
                          ? mod.max_rounds
                          : '-';
                      const roundDurationLabel = formatRoundDuration(
                        mod.time_limit_seconds,
                      );

                      return (
                        <ModalityCard key={mod.id}>
                          {imgSrc && (
                            <ModalityImage src={imgSrc} alt={modalityName} />
                          )}
                          <ModalityInfo>
                            <ModalityName>{modalityName}</ModalityName>
                            <ModalityMeta>{`Rodadas: ${maxRoundsLabel}`}</ModalityMeta>
                            <ModalityMeta>{`Tempo por rodada: ${roundDurationLabel}`}</ModalityMeta>
                            <CategoryBadgeRow>
                              {mod.categories?.map(cat => (
                                <CategoryBadge key={cat.id}>
                                  <MiniBotIcon
                                    width="0.85rem"
                                    strokeWidth={2}
                                    stroke="#1e88e1"
                                  />
                                  {cat.name ??
                                    CATEGORY_NUMBER_TO_LABEL[cat.number]?.split(
                                      ':',
                                    )[0] ??
                                    `Categoria ${cat.number}`}
                                </CategoryBadge>
                              ))}
                            </CategoryBadgeRow>
                          </ModalityInfo>
                        </ModalityCard>
                      );
                    })}
                  </ModalitiesRow>

                  <ActionButtonsRow>
                    <PrimaryButton
                      type="button"
                      onClick={() => navigate(`/tournament/${tournament.id}`)}
                    >
                      Acessar Torneio
                    </PrimaryButton>
                  </ActionButtonsRow>
                </TournamentInfo>
              </MainCardContent>
            </MainCard>
          );
        })}
      </TournamentList>
    </Container>
  );
};

export default ListTournaments;
